import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Pencil, X, TrendingUp, TrendingDown, Trophy, ThumbsDown,
  Calendar, Clock, DollarSign, Package,
} from "lucide-react";

// ── Formatting utilities ─────────────────────────────────────────────────────
export function fmt(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
}
export function parseBRNumber(s) {
  if (typeof s === "number") return s;
  if (!s) return 0;
  return parseFloat(String(s).replace(/\./g, "").replace(",", ".")) || 0;
}
export function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  const time = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return `${date} ${time}`;
}
export function fmtDateShort(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// ── Inline editable cell ─────────────────────────────────────────────────────
export function InlineCell({ value, onSave, format = "number", className = "" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState("");
  const start = () => { setVal(String(value ?? "")); setEditing(true); };
  const save  = () => { const p = parseFloat(String(val).replace(",",".")); if (!isNaN(p) && onSave) onSave(p); setEditing(false); };
  if (editing) return (
    <input autoFocus value={val} onChange={e => setVal(e.target.value)}
      onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
      onBlur={save}
      className={`w-full text-xs font-semibold border-b border-blue-400 outline-none bg-transparent ${className}`} />
  );
  return (
    <span onClick={start} className={`cursor-pointer hover:text-blue-500 transition-colors group inline-flex items-center gap-0.5 ${className}`}>
      {format === "currency" ? fmt(value) : value}
      <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-50 transition-opacity flex-shrink-0" />
    </span>
  );
}

// ── Product line row ──────────────────────────────────────────────────────────
export function ProdutoLine({ line, onUpdate, onDelete }) {
  return (
    <div className="grid grid-cols-12 gap-2 px-5 py-2.5 items-center hover:bg-slate-50/60 transition-colors">
      <span className="col-span-4 text-xs text-slate-700 font-medium truncate">{line.produto_nome || "—"}</span>
      <div className="col-span-2 flex justify-center">
        <InlineCell value={line.quantidade} onSave={v => onUpdate("quantidade", v)} className="text-center text-xs" />
      </div>
      <div className="col-span-3 flex justify-end">
        <InlineCell value={line.valor_unitario} onSave={v => onUpdate("valor_unitario", v)} format="currency" className="text-xs text-right" />
      </div>
      <span className="col-span-2 text-right text-xs font-semibold text-slate-700">{fmt(line.valor_total)}</span>
      <div className="col-span-1 flex justify-end">
        <button type="button" onClick={onDelete} className="text-slate-300 hover:text-rose-500 transition-colors p-0.5">
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

export function MiniCard({ label, value, sub }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 min-w-[88px]">
      <p className="text-[11px] text-slate-400 mb-1">{label}</p>
      <p className="text-sm font-bold text-slate-800 leading-none">{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

export function MetricCard({ label, value, sub, icon: Icon, breach = false, tc500 = "#F97316" }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border bg-white p-4 transition-all"
      style={breach
        ? { borderStyle: "dashed", borderColor: "#fbbf24", backgroundColor: "#fffbeb" }
        : { borderStyle: "solid", borderColor: "#e2e8f0" }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" style={{ color: breach ? "#f59e0b" : "#94a3b8" }} />
        <span className="text-xs font-medium" style={{ color: breach ? "#b45309" : "#64748b" }}>{label}</span>
      </div>
      <p className="text-xl font-bold" style={{ color: breach ? "#92400e" : "#1e293b" }}>{value}</p>
      {sub && <p className="text-xs mt-0.5" style={{ color: breach ? "#d97706" : "#94a3b8" }}>{sub}</p>}
      {breach && <p className="text-[11px] font-medium mt-1" style={{ color: "#d97706" }}>{t("sla.exceeded")}</p>}
    </div>
  );
}

export function EmpresaCard({ totalAbertos, totalGanhos, totalPerdidos, tc500 }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-4 h-4 text-slate-400" />
        <span className="text-xs font-medium text-slate-500">Negócios da empresa</span>
      </div>
      <p className="text-xl font-bold text-slate-800 mt-1">{totalAbertos}</p>
      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-100">
        <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
          <Trophy className="w-3 h-3" />{totalGanhos} ganhos
        </span>
        <span className="flex items-center gap-1 text-[11px] font-semibold text-rose-500">
          <ThumbsDown className="w-3 h-3" />{totalPerdidos} perdidos
        </span>
      </div>
    </div>
  );
}

export function UltimoNegocioCard({ data, haDias }) {
  const isPrimeiro = !data;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <Calendar className="w-4 h-4 text-slate-400" />
        <span className="text-xs font-medium text-slate-500">Último negócio</span>
      </div>
      <p className={`text-xl font-bold mt-1 ${isPrimeiro ? "text-slate-400 text-base" : "text-slate-800"}`}>
        {isPrimeiro ? "Este é o primeiro negócio." : data}
      </p>
      <div className="mt-2 pt-2 border-t border-slate-100">
        {haDias ? (
          <p className="text-[11px] text-slate-400 flex items-center gap-1">
            <Clock className="w-3 h-3 flex-shrink-0" />{haDias}
          </p>
        ) : (
          <p className="text-[11px] text-slate-300">Sem dados para análise.</p>
        )}
      </div>
    </div>
  );
}

export function CompareCard({ label, value, avgValue, icon: Icon, isCount = false, isText = false, isEditable = false, isCalc = false, onSave, subFooter = null, themeColor = "#3b82f6" }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState("");

  const diff = (!isCount && !isText && avgValue > 0)
    ? ((value - avgValue) / avgValue * 100) : null;
  const isAbove = diff !== null && diff > 0.5;
  const isBelow = diff !== null && diff < -0.5;
  const displayValue = isText ? value : isCount ? value : fmt(value);

  const handleStartEdit = () => {
    if (!isEditable || isCalc) return;
    setEditVal(isCount ? String(value) : String(value ?? 0));
    setEditing(true);
  };
  const handleSave = () => {
    const parsed = parseFloat(String(editVal).replace(",", "."));
    if (!isNaN(parsed) && onSave) onSave(parsed);
    setEditing(false);
  };

  return (
    <div className={`rounded-xl border bg-white p-4 transition-all ${isEditable && !isCalc ? "border-slate-200 hover:shadow-sm cursor-pointer group" : "border-slate-200"}`}
      style={isEditable && !isCalc ? { "--tc": themeColor } : undefined}
      onMouseEnter={e => { if (isEditable && !isCalc) e.currentTarget.style.borderColor = themeColor; }}
      onMouseLeave={e => { if (isEditable && !isCalc) e.currentTarget.style.borderColor = ""; }}
      onClick={!editing ? handleStartEdit : undefined}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-400" />
          <span className="text-xs font-medium text-slate-500">{label}</span>
        </div>
        {isCalc && <span className="text-[9px] text-slate-300 font-medium uppercase tracking-wider">auto</span>}
        {isEditable && !isCalc && !editing && <Pencil className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-all" style={{ color: themeColor }} />}
      </div>
      {editing ? (
        <div className="flex items-center gap-1.5 mt-1" onClick={e => e.stopPropagation()}>
          <input autoFocus value={editVal} onChange={e => setEditVal(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") setEditing(false); }}
            onBlur={handleSave}
            className="w-full text-xl font-bold text-slate-800 border-b-2 outline-none bg-transparent" style={{ borderColor: themeColor }} />
        </div>
      ) : (
        <p className={`text-xl font-bold mt-1 ${isCalc ? "text-slate-400" : "text-slate-800"}`}>{displayValue}</p>
      )}
      {subFooter ? (
        <p className="text-[11px] text-slate-400 mt-2 pt-2 border-t border-slate-100">{subFooter}</p>
      ) : avgValue != null && avgValue > 0 && !isCount && !isText ? (
        <div className="flex items-center justify-between gap-1 mt-2 pt-2 border-t border-slate-100">
          <p className="text-[11px] text-slate-400">
            Média: <span className="font-medium text-slate-500">{fmt(avgValue)}</span>
          </p>
          {diff !== null && (
            <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${isAbove ? "bg-emerald-50 text-emerald-600" : isBelow ? "bg-rose-50 text-rose-500" : "text-slate-400"}`}>
              {isAbove ? <TrendingUp className="w-2.5 h-2.5" /> : isBelow ? <TrendingDown className="w-2.5 h-2.5" /> : null}
              {isAbove ? `+${Math.round(diff)}%` : isBelow ? `${Math.round(diff)}%` : "—"}
            </span>
          )}
        </div>
      ) : !isText && !isCount && !subFooter ? (
        <p className="text-[11px] text-slate-300 mt-2 pt-2 border-t border-slate-100">Sem dados para análise.</p>
      ) : null}
    </div>
  );
}
